import { useState } from 'react';
import { TrendingUp, Zap } from 'lucide-react';
import { Button } from '@ethsign/ui';
import type { RewardMode } from '@/types';
import { formatAmount } from '@/utils/common';

interface RewardChoiceProps {
  totalRewards: string;
  onModeSelect: (mode: RewardMode) => void;
  loading: boolean;
}

export function RewardChoice({ totalRewards, onModeSelect, loading }: RewardChoiceProps) {
  const [selectedMode, setSelectedMode] = useState<RewardMode>(null);

  const immediateAmount = (parseFloat(totalRewards) * 0.3).toString();

  const handleModeSelect = (mode: RewardMode) => {
    setSelectedMode(mode);
  };

  const handleConfirm = () => {
    if (selectedMode) {
      onModeSelect(selectedMode);
    }
  };

  return (
    <div className="w-full max-w-4xl space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold">Choose Your Reward Mode</h2>
        <p className="text-muted-foreground">Select how you want to claim your TAC rewards</p>
      </div>

      <div className="flex flex-col gap-6">
        {/* Staking Mode */}
        <div
          className={`bg-card border rounded-md p-6 cursor-pointer transition-all ${
            selectedMode === 'staking' ? 'ring-2 ring-primary border-primary' : 'hover:shadow-md'
          }`}
          onClick={() => handleModeSelect('staking')}
        >
          <div className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            <span>Staking and Vesting mode</span>
          </div>

          <div className="space-y-4">
            <div className="mt-2">
              <p className="text-sm text-muted-foreground">Base Rewards (Linear Release)</p>
              <p className="text-xl font-bold">{formatAmount(totalRewards)} TAC (100%)</p>
            </div>

            <div className="text-sm text-muted-foreground space-y-1">
              <p>• Stake your full allocation with a validator for linear vesting </p>
              <p>• earn additional staking rewards of ~8% APY.</p>
              <p>• 60-day vesting period with 21-day unbonding time required</p>
            </div>

            {/* <div className="text-sm text-muted-foreground space-y-1">
              <p>• All rewards staked to earn additional yield</p>
              <p>• 3-month linear release schedule</p>
              <p>• Earn staking rewards on delegated amount</p>
              <p>• Can claim staking rewards anytime</p>
              <p>• Can undelegate available amounts</p>
            </div>

            <div className="flex items-center space-x-1 text-xs text-muted-foreground">
              <Info className="h-3 w-3" />
              <span>Higher potential returns with staking rewards</span>
            </div>

            {selectedValidator && (
              <div className="bg-muted p-3 rounded-md">
                <p className="text-xs text-muted-foreground">Recommended Validator</p>
                <p className="text-sm font-medium">{selectedValidator.name}</p>
                <p className="text-xs text-muted-foreground">Commission: {selectedValidator.commission}</p>
              </div>
            )} */}
          </div>
        </div>
        {/* Immediate Withdraw Mode */}
        <div
          className={`bg-card border rounded-md p-6 cursor-pointer transition-all ${
            selectedMode === 'immediate' ? 'ring-2 ring-primary border-primary' : 'hover:shadow-md'
          }`}
          onClick={() => handleModeSelect('immediate')}
        >
          <div className="flex items-center space-x-2">
            <Zap className="h-5 w-5 text-yellow-600" />
            <span>Immediate Withdraw</span>
          </div>
          <div className="space-y-4 mt-2">
            <div>
              <p className="text-sm text-muted-foreground">Immediate</p>
              <p className="text-xl font-bold">{formatAmount(immediateAmount)} TAC (30%)</p>
            </div>

            <div className="text-sm text-muted-foreground space-y-1">
              <p>• Get instant 30% of your allocations </p>
              <p>• Forfeiting the remaining 70%. </p>
              <p>• This is a one-time withdrawal with no staking benefits.</p>
            </div>

            {/* <div className="flex items-center space-x-1 text-xs text-muted-foreground">
              <Info className="h-3 w-3" />
              <span>Simple and predictable returns</span>
            </div>  */}
          </div>
        </div>
      </div>

      {selectedMode && (
        <div className="text-center">
          <Button onClick={handleConfirm} size="lg" className="px-8" loading={loading}>
            Continue with {selectedMode === 'immediate' ? 'Immediate Withdraw' : 'Staking Mode'}
          </Button>
        </div>
      )}
    </div>
  );
}
